import { type SortFunctions } from '@optimization/ssi-common';
import { type VehicleCycledEnergyRecord } from '@/features/BatteryMonitoring/services/battery-monitoring-api/types/battery-monitoring-api';
import { type VehicleListColumnKey } from '@/features/BatteryMonitoring/VehicleList';

// helper functions
const parseNumericString = (value: string): number => {
  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? Number.POSITIVE_INFINITY : numberValue;
};

const getSpecification = (vehicle: SortVehicle): string =>
  `${vehicle.batteryCapacity ?? ''} ${vehicle.configuration}`.trim();

// sorting functions
const sortCustomer = <Type extends SortVehicle>(a: Type, b: Type, direction: 'asc' | 'desc'): number => {
  const aHasCustomer = Boolean(a.customer.trim());
  const bHasCustomer = Boolean(b.customer.trim());

  if (aHasCustomer !== bHasCustomer) {
    const namedCustomerComesFirst = direction === 'asc';
    return aHasCustomer === namedCustomerComesFirst ? -1 : 1;
  }

  return direction === 'asc' ? a.customer.localeCompare(b.customer) : b.customer.localeCompare(a.customer);
};

const sortCustomerAsc = <Type extends SortVehicle>(a: Type, b: Type): number => sortCustomer(a, b, 'asc');

const sortCustomerDesc = <Type extends SortVehicle>(a: Type, b: Type): number => sortCustomer(a, b, 'desc');

const sortChassisAsc = <Type extends SortVehicle>(a: Type, b: Type): number => {
  const aNumber = parseNumericString(a.chassisNumber);
  const bNumber = parseNumericString(b.chassisNumber);

  if (aNumber !== bNumber) {
    return aNumber - bNumber;
  }

  return a.chassisNumber.localeCompare(b.chassisNumber);
};

const sortChassisDesc = <Type extends SortVehicle>(a: Type, b: Type): number => {
  const aNumber = parseNumericString(a.chassisNumber);
  const bNumber = parseNumericString(b.chassisNumber);

  if (aNumber !== bNumber) {
    return bNumber - aNumber;
  }

  return b.chassisNumber.localeCompare(a.chassisNumber);
};

const sortVehicleTypeAsc = <Type extends SortVehicle>(a: Type, b: Type): number =>
  a.vehicleType.localeCompare(b.vehicleType);

const sortVehicleTypeDesc = <Type extends SortVehicle>(a: Type, b: Type): number =>
  b.vehicleType.localeCompare(a.vehicleType);

const sortCustomerCountryAsc = <Type extends SortVehicle>(a: Type, b: Type): number =>
  a.customerCountry.localeCompare(b.customerCountry);

const sortCustomerCountryDesc = <Type extends SortVehicle>(a: Type, b: Type): number =>
  b.customerCountry.localeCompare(a.customerCountry);

const sortCycledEnergyKwhAsc = <Type extends SortVehicle>(a: Type, b: Type): number =>
  (a.cycledEnergyKwh ?? 0) - (b.cycledEnergyKwh ?? 0);

const sortCycledEnergyKwhDesc = <Type extends SortVehicle>(a: Type, b: Type): number =>
  (b.cycledEnergyKwh ?? 0) - (a.cycledEnergyKwh ?? 0);

const sortCycledEnergyPerKmAsc = <Type extends SortVehicle>(a: Type, b: Type): number =>
  (a.cycledEnergyPerKm ?? 0) - (b.cycledEnergyPerKm ?? 0);

const sortCycledEnergyPerKmDesc = <Type extends SortVehicle>(a: Type, b: Type): number =>
  (b.cycledEnergyPerKm ?? 0) - (a.cycledEnergyPerKm ?? 0);

const sortStateOfHealthAsc = <Type extends SortVehicle>(a: Type, b: Type): number =>
  (a.stateOfHealthRatio ?? 0) - (b.stateOfHealthRatio ?? 0);

const sortStateOfHealthDesc = <Type extends SortVehicle>(a: Type, b: Type): number =>
  (b.stateOfHealthRatio ?? 0) - (a.stateOfHealthRatio ?? 0);

const sortAssemblyDateAsc = <Type extends SortVehicle>(a: Type, b: Type): number =>
  new Date(a.assemblyDate).getTime() - new Date(b.assemblyDate).getTime();

const sortAssemblyDateDesc = <Type extends SortVehicle>(a: Type, b: Type): number =>
  new Date(b.assemblyDate).getTime() - new Date(a.assemblyDate).getTime();

const sortSpecificationAsc = <Type extends SortVehicle>(a: Type, b: Type): number =>
  getSpecification(a).localeCompare(getSpecification(b));

const sortSpecificationDesc = <Type extends SortVehicle>(a: Type, b: Type): number =>
  getSpecification(b).localeCompare(getSpecification(a));

const sortStatusAsc = <T extends SortVehicle>(a: T, b: T): number => Number(a.vehicleAlert) - Number(b.vehicleAlert);

const sortStatusDesc = <T extends SortVehicle>(a: T, b: T): number => Number(b.vehicleAlert) - Number(a.vehicleAlert);

export type SortVehicle = Pick<
  VehicleCycledEnergyRecord,
  | 'customer'
  | 'chassisNumber'
  | 'vehicleType'
  | 'customerCountry'
  | 'cycledEnergyKwh'
  | 'cycledEnergyPerKm'
  | 'stateOfHealthRatio'
  | 'assemblyDate'
  | 'batteryCapacity'
  | 'configuration'
  | 'vehicleAlert'
  | 'topography'
  | 'temperatureMedianC'
  | 'traffic'
  | 'averageSpeedKph'
  | 'gtwQ3Tonnes'
  | 'dailyDistanceQ3Km'
  | 'totalEcPerKm'
>;

// Missing operational values stay last in both directions; zero remains a real value.
type OperationalValue = string | number | null | undefined;

const compareOperationalValues = (
  left: OperationalValue,
  right: OperationalValue,
  direction: 'asc' | 'desc',
): number => {
  const leftMissing = left === null || left === undefined;
  const rightMissing = right === null || right === undefined;
  if (leftMissing && rightMissing) return 0;
  if (leftMissing) return 1;
  if (rightMissing) return -1;
  const comparison =
    typeof left === 'number' && typeof right === 'number' ? left - right : String(left).localeCompare(String(right));
  return direction === 'asc' ? comparison : -comparison;
};

const operationalSort = (
  select: (vehicle: SortVehicle) => OperationalValue,
): SortFunctions<SortVehicle, 'totalEcPerKm'>['totalEcPerKm'] => ({
  asc: (left: SortVehicle, right: SortVehicle) => compareOperationalValues(select(left), select(right), 'asc'),
  desc: (left: SortVehicle, right: SortVehicle) => compareOperationalValues(select(left), select(right), 'desc'),
});

export const sortFunctions: SortFunctions<SortVehicle, VehicleListColumnKey> = {
  topography: operationalSort((vehicle) => vehicle.topography),
  temperatureMedianC: operationalSort((vehicle) => vehicle.temperatureMedianC),
  traffic: {
    asc: (left, right) =>
      compareOperationalValues(left.traffic, right.traffic, 'asc') ||
      compareOperationalValues(left.averageSpeedKph, right.averageSpeedKph, 'asc'),
    desc: (left, right) =>
      compareOperationalValues(left.traffic, right.traffic, 'desc') ||
      compareOperationalValues(left.averageSpeedKph, right.averageSpeedKph, 'desc'),
  },
  gtwQ3Tonnes: operationalSort((vehicle) => vehicle.gtwQ3Tonnes),
  dailyDistanceQ3Km: operationalSort((vehicle) => vehicle.dailyDistanceQ3Km),
  totalEcPerKm: operationalSort((vehicle) => (vehicle.totalEcPerKm === 0 ? null : vehicle.totalEcPerKm)),
  customer: {
    asc: sortCustomerAsc,
    desc: sortCustomerDesc,
  },
  chassisNumber: {
    asc: sortChassisAsc,
    desc: sortChassisDesc,
  },
  vehicleType: {
    asc: sortVehicleTypeAsc,
    desc: sortVehicleTypeDesc,
  },
  country: {
    asc: sortCustomerCountryAsc,
    desc: sortCustomerCountryDesc,
  },
  cycledEnergy: {
    asc: sortCycledEnergyKwhAsc,
    desc: sortCycledEnergyKwhDesc,
  },
  cycledEnergyPerKm: {
    asc: sortCycledEnergyPerKmAsc,
    desc: sortCycledEnergyPerKmDesc,
  },
  stateOfHealth: {
    asc: sortStateOfHealthAsc,
    desc: sortStateOfHealthDesc,
  },
  assemblyDate: {
    asc: sortAssemblyDateAsc,
    desc: sortAssemblyDateDesc,
  },
  specification: {
    asc: sortSpecificationAsc,
    desc: sortSpecificationDesc,
  },
  status: {
    asc: sortStatusAsc,
    desc: sortStatusDesc,
  },
};
